import React, { useEffect, useState } from "react";
import {
  Wallet, TrendingUp, ArrowDownToLine, Loader2, Zap, ShieldCheck, ExternalLink,
  ChevronRight, DollarSign, Percent, Info, Coins,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * EarningsPanel — streamer's withdrawable balance + lifetime stats + payout.
 *
 * Renders three cards + a recent-transactions list + a big Withdraw button.
 * Safe to embed anywhere for authenticated users; non-streamers see a
 * "Complete KYC to earn" prompt instead of numbers.
 */
export default function EarningsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/earnings/summary")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const doWithdraw = async () => {
    if (!data?.can_withdraw || withdrawing) return;
    setWithdrawing(true);
    try {
      const r = await api.post("/earnings/withdraw", {});
      toast.success(`Payout initiated: $${r.data.amount_usd.toFixed(2)}${r.data.mock ? " (test mode)" : ""}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Withdrawal failed");
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading) {
    return (
      <div className="term-card p-6 text-center" data-testid="earnings-loading">
        <Loader2 className="w-5 h-5 mx-auto animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!data) return null;

  const {
    available_cents, available_usd, can_withdraw,
    kyc_verified, connect_onboarded,
    min_withdrawal_cents, min_withdrawal_usd,
    lifetime_earned_cents, lifetime_paid_out_cents, lifetime_platform_fees_cents,
    policy,
    recent_transactions,
  } = data;

  // New per-type revenue policy display — tips are 100% streamer,
  // channel subs are 70/30, platform subs are 100% platform.
  const tipPct = policy?.tip?.streamer_pct ?? 100;
  const channelSubPct = policy?.channel_sub?.streamer_pct ?? 70;

  return (
    <div className="space-y-4" data-testid="earnings-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="sc-label" style={{ color: "var(--sc-volt)" }}>
            ◤ CREATOR EARNINGS
          </div>
          <h2 className="sc-heading text-2xl font-black tracking-tight mt-1">Your Balance</h2>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-[10px] sc-mono text-zinc-500 hover:text-zinc-300 transition"
          data-testid="earnings-refresh"
        >
          REFRESH
        </button>
      </div>

      {/* Prerequisites */}
      {(!kyc_verified || !connect_onboarded) && (
        <div className="term-card orange p-4" data-testid="earnings-prereq">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "var(--sc-orange)" }} />
            <div className="flex-1">
              <div className="sc-heading text-sm font-bold mb-1">Finish setup to withdraw</div>
              <div className="text-xs text-zinc-400 leading-relaxed">
                Complete these one-time steps before you can move money to your bank:
              </div>
              <div className="mt-3 space-y-2">
                <PrereqRow done={kyc_verified} label="Stripe Identity verification (KYC)" to="/account" />
                <PrereqRow done={connect_onboarded} label="Stripe Connect Express onboarding" to="/account" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Balance cards */}
      <div className="grid sm:grid-cols-3 gap-3">
        <BalanceCard
          icon={Wallet}
          color="volt"
          label="AVAILABLE"
          value={`$${available_usd.toFixed(2)}`}
          hint={available_cents >= min_withdrawal_cents ? "Ready to withdraw" : `Min $${min_withdrawal_usd.toFixed(2)} to withdraw`}
          testid="earnings-available"
        />
        <BalanceCard
          icon={TrendingUp}
          color="cyan"
          label="LIFETIME EARNED"
          value={`$${(lifetime_earned_cents / 100).toFixed(2)}`}
          hint={`70% of $${((lifetime_earned_cents + lifetime_platform_fees_cents) / 100).toFixed(2)} gross`}
          testid="earnings-lifetime"
        />
        <BalanceCard
          icon={ArrowDownToLine}
          color="pink"
          label="PAID OUT"
          value={`$${(lifetime_paid_out_cents / 100).toFixed(2)}`}
          hint={`Deposited to your bank`}
          testid="earnings-paid-out"
        />
      </div>

      {/* Withdraw CTA */}
      <div
        className="term-card p-5"
        style={{
          background: can_withdraw
            ? "linear-gradient(135deg, rgba(204,255,0,0.08), rgba(0,240,255,0.04))"
            : undefined,
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="sc-heading text-lg font-black">Withdraw to your bank</div>
            <div className="text-xs text-zinc-400 mt-1">
              Payouts are processed via Stripe Connect Express. Typical arrival: 2-3 business days.
            </div>
          </div>
          <button
            type="button"
            onClick={doWithdraw}
            disabled={!can_withdraw || withdrawing}
            data-testid="earnings-withdraw-btn"
            className="sc-btn text-base flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {withdrawing ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
            ) : (
              <><ArrowDownToLine className="w-4 h-4" /> Withdraw ${available_usd.toFixed(2)}</>
            )}
          </button>
        </div>
        {!can_withdraw && (
          <div className="text-[11px] text-zinc-500 mt-3 flex items-center gap-1.5">
            <Info className="w-3 h-3" />
            {available_cents < min_withdrawal_cents
              ? `Reach $${min_withdrawal_usd.toFixed(2)} to enable withdrawals.`
              : !kyc_verified
                ? "Complete KYC identity verification first."
                : !connect_onboarded
                  ? "Complete Stripe Connect onboarding first."
                  : "Withdrawal not available right now."}
          </div>
        )}
      </div>

      {/* Split explainer — per-event policy so the split is unambiguous. */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="term-card cyan p-4" data-testid="earnings-policy">
          <div className="flex items-center gap-2 mb-2">
            <Percent className="w-3.5 h-3.5" style={{ color: "var(--sc-cyan)" }} />
            <span className="sc-label text-[10px]" style={{ color: "var(--sc-cyan)" }}>REVENUE SPLIT</span>
          </div>
          <div className="text-sm text-zinc-300 space-y-1">
            <div>
              <span className="sc-heading font-black" style={{ color: "var(--sc-volt)" }}>{tipPct}%</span>{" "}
              <span className="text-zinc-500">to you on</span> <span className="text-zinc-300">tips</span>
            </div>
            <div>
              <span className="sc-heading font-black" style={{ color: "var(--sc-volt)" }}>{channelSubPct}%</span>{" "}
              <span className="text-zinc-500">to you on</span> <span className="text-zinc-300">channel subscriptions</span>
            </div>
          </div>
          <div className="text-[11px] text-zinc-500 mt-2">
            Platform-wide (Plus / Premium) subs are not shared with streamers.
          </div>
        </div>
        <div className="term-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-3.5 h-3.5" style={{ color: "var(--sc-orange)" }} />
            <span className="sc-label text-[10px]" style={{ color: "var(--sc-orange)" }}>CONVERSION</span>
          </div>
          <div className="text-sm text-zinc-300">
            <span className="sc-heading font-black">100 GC</span> <span className="text-zinc-500">= </span>
            <span className="sc-heading font-black">$1.00 USD</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">
            Tips and Gold Coin subs convert at this rate for payouts.
          </div>
        </div>
      </div>

      {/* Recent transactions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="sc-label" style={{ color: "var(--sc-cyan)" }}>◈ RECENT TRANSACTIONS</div>
          <Link to="/dashboard/transactions" className="text-[10px] sc-mono text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
            VIEW ALL <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        {recent_transactions.length === 0 ? (
          <div className="term-card p-6 text-center text-xs text-zinc-500" data-testid="earnings-tx-empty">
            <Coins className="w-6 h-6 mx-auto mb-2 text-zinc-700" />
            No earnings yet. Start streaming and receive your first tip!
          </div>
        ) : (
          <div className="term-card p-0 overflow-hidden">
            <table className="w-full text-xs" data-testid="earnings-tx-table">
              <thead>
                <tr className="border-b border-white/10 text-zinc-500 sc-mono text-[10px]">
                  <th className="text-left px-3 py-2">DATE</th>
                  <th className="text-left px-3 py-2">TYPE</th>
                  <th className="text-left px-3 py-2">FROM</th>
                  <th className="text-right px-3 py-2">GROSS</th>
                  <th className="text-right px-3 py-2">YOUR SHARE</th>
                  <th className="text-center px-3 py-2">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {recent_transactions.map((tx) => (
                  <TxRow key={tx.id} tx={tx} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function BalanceCard({ icon: Icon, color, label, value, hint, testid }) {
  return (
    <div className={`term-card ${color} p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5" style={{ color: `var(--sc-${color})` }} />
        <span className="sc-label text-[10px]" style={{ color: `var(--sc-${color})` }}>{label}</span>
      </div>
      <div className="sc-heading text-2xl font-black tracking-tight">{value}</div>
      <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>
    </div>
  );
}

function PrereqRow({ done, label, to }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={`w-4 h-4 rounded-full flex items-center justify-center border ${done ? "border-[color:var(--sc-volt)] bg-[color:var(--sc-volt)]/10" : "border-zinc-600"}`}
      >
        {done && <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--sc-volt)]" />}
      </span>
      <span className={`text-xs ${done ? "text-zinc-400 line-through" : "text-zinc-200"}`}>
        {label}
      </span>
      {!done && (
        <Link to={to} className="text-[10px] sc-mono text-[color:var(--sc-cyan)] hover:underline ml-auto flex items-center gap-1">
          COMPLETE <ExternalLink className="w-2.5 h-2.5" />
        </Link>
      )}
    </div>
  );
}

const TYPE_LABELS = {
  tip: { label: "Tip", color: "pink" },
  channel_sub_gc: { label: "Channel Sub (GC)", color: "cyan" },
  channel_sub_stripe: { label: "Channel Sub (Stripe)", color: "cyan" },
  platform_sub_gc: { label: "Platform Sub (GC)", color: "volt" },
  platform_sub_stripe: { label: "Platform Sub", color: "volt" },
  sub_stripe: { label: "Platform Sub", color: "volt" },
  coin_pack_sale: { label: "Coin Pack", color: "orange" },
  credit_pack: { label: "Coin Pack", color: "orange" },
  payout: { label: "Payout", color: "orange" },
  reversal: { label: "Reversal", color: "pink" },
};

function TxRow({ tx }) {
  const meta = TYPE_LABELS[tx.type] || { label: tx.type, color: "cyan" };
  const isPayout = tx.type === "payout";
  return (
    <tr className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
      <td className="px-3 py-2 sc-mono text-zinc-500">
        {new Date(tx.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
      </td>
      <td className="px-3 py-2">
        <span
          className="sc-mono text-[10px] px-1.5 py-0.5 rounded"
          style={{ background: `var(--sc-${meta.color})22`, color: `var(--sc-${meta.color})` }}
        >
          {meta.label}
        </span>
      </td>
      <td className="px-3 py-2 text-zinc-400 truncate max-w-[120px]">
        {isPayout ? "→ your bank" : (tx.from_username || "—")}
      </td>
      <td className="px-3 py-2 text-right sc-mono text-zinc-400">
        ${(tx.gross_usd_cents / 100).toFixed(2)}
      </td>
      <td
        className="px-3 py-2 text-right sc-mono font-bold"
        style={{ color: isPayout ? "var(--sc-orange)" : "var(--sc-volt)" }}
      >
        {isPayout ? "-" : "+"}${(tx.streamer_share_cents / 100).toFixed(2)}
      </td>
      <td className="px-3 py-2 text-center">
        <span className={`text-[9px] sc-mono ${tx.paid_out ? "text-zinc-500" : "text-[color:var(--sc-volt)]"}`}>
          {tx.paid_out ? "PAID" : "PENDING"}
        </span>
      </td>
    </tr>
  );
}
